import React, { useCallback, useEffect, useMemo, useState } from "react";
import {Grid} from "@mui/material";
import {useTranslation} from "react-i18next";
import {Application} from "../Application";
import {ContentPageTableHeader} from "../../../shared/components/tables/ContentPageTableHeader";
import {StandardRole} from "../../users/Role";
import {FormDialog} from "../../../shared/components/dialogs/FormDialog";
import {defaultLicense, License} from "./License";
import {LicenseForm} from "./LicenseForm";
import {useAxios} from "../../../shared/technical/rest/AxiosProvider";
import {createLicense, deleteLicense, getAllLicenses, updateLicense} from "./LicenseApi";
import {useMutation, useQuery, useQueryClient} from "react-query";
import {MutateOptions} from "react-query/types/core/types";
import {ContentPageTable} from "../../../shared/components/tables/ContentPageTable";
import {licenseColumns} from "./LicenseColumns";
import {isEditableForRoles} from "../../../shared/technical/security/WithRestrictedEdit";
import {useAccount} from "../../users/AccountProvider";
import {ApplicationSubPageContentContainer} from "../ApplicationSubPageContentContainer";
import {MUIDataTableOptions} from "mui-datatables";

export function LicensesSubPage(props: {
  application: Application,
}) {
  const {application} = props;
  const {t} = useTranslation();
  const axios = useAxios();
  const account = useAccount();
  const queryClient = useQueryClient();
  const [showFormDialog, setShowFormDialog] = useState(false);
  const [actualEntity, setActualEntity] = useState<License>(defaultLicense(application, t))
  const [tableColumns] = useState(licenseColumns(t));
  const tableOptions: MUIDataTableOptions = {
    filter: false,
    storageKey: 'licenses',
  };

  useEffect(() => {
    // Reset page to 0 when the application changes
    tableOptions.page = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {data} = useQuery('licenses', () => getAllLicenses(axios, application.id!!), {keepPreviousData: true, refetchOnWindowFocus: false});
  const updateApplicationMutation = useMutation((license: License) =>
    updateLicense(axios, application.id!!, license));
  const createApplicationMutation = useMutation((license: License) =>
    createLicense(axios, application.id!!, license));
  const deleteApplicationMutation = useMutation((license: License) =>
    deleteLicense(axios, application.id!!, license.id!!));

  const mutateOptions: MutateOptions<any, any, License> = useMemo(() => ({
    onSuccess: () => {
      queryClient.invalidateQueries('licenses').then();
    }
  }), [queryClient]);

  function handleCloseDialog(event?: {}, reason?: 'backdropClick' | 'escapeKeyDown') {
    if (reason === 'backdropClick') {
      console.info('backdrop click');
      return;
    }
    setShowFormDialog(false);
  }

  const handleSave = useCallback(async (entity: License) => {
    if (entity.id) {
      try {
        setShowFormDialog(false);
        updateApplicationMutation.mutate(entity, mutateOptions);
        return
      } catch (error) {
        console.error('Could not update entity', error);
        return Promise.reject(error);
      }
    } else {
      setShowFormDialog(false);
      createApplicationMutation.mutate(entity, mutateOptions);
      return
    }
  }, [setShowFormDialog, updateApplicationMutation, createApplicationMutation, mutateOptions]);

  const handleDelete = useCallback(async (entity: License) => {
    if (!entity.id) {
      return Promise.resolve()
    }
    try {
      setShowFormDialog(false);
      deleteApplicationMutation.mutate(entity, mutateOptions);
    } catch (error) {
      console.error('Could not delete entity', error);
      setShowFormDialog(false);
    }
  }, [setShowFormDialog, deleteApplicationMutation, mutateOptions]);

  const handleAddButtonClick = useCallback(() => {
    setActualEntity(defaultLicense(application, t));
    setShowFormDialog(true);
  }, [application, t]);

  const handleSelectLicense = useCallback((license: License) => {
    setActualEntity(license);
    setShowFormDialog(true);
  }, [setShowFormDialog, setActualEntity]);

  return (
    <ApplicationSubPageContentContainer>
      <ContentPageTableHeader onClick={handleAddButtonClick}
                              title={t('License.titleLong')}
                              titleButton={t('License.buttonCreate')}
                              isEditable={isEditableForRoles(account, [StandardRole.ADMIN, StandardRole.APPLICATION_ADMIN])} />
      <Grid container={true} sx={{marginTop: 1}} spacing={2}>
        <Grid item={true} xs={12} sx={{mx: 2}}>
          <ContentPageTable entities={data || []} columns={tableColumns} handleRowClick={handleSelectLicense}
                            tableOptions={tableOptions} tableGridSize={{}} />
        </Grid>
      </Grid>
      <FormDialog open={showFormDialog} handleClose={handleCloseDialog}>
        <LicenseForm application={application} license={actualEntity}
                     handleSave={handleSave} handleDelete={handleDelete} handleClose={handleCloseDialog} />
      </FormDialog>
    </ApplicationSubPageContentContainer>
  )
}
